/**
 * Daily Group Performance column order:
 * Group -> Attempts -> Missed -> Canceled -> Completed ->
 * consultation stage -> same-day conversion -> spending.
 */

export const DAILY_GROUP_ORDER = ["a", "b", "c", "n", "dead" ,"query"];
export const DAILY_GROUP_LABELS = {
  a: "Group A",
  b: "Group B",
  c: "Group C",
  n: "Group N",
  new: "Group N",
  dead: "Group Dead",
  query : "Query"
};

const GROUP_API_ALIASES = {
  a: ["a"],
  b: ["b"],
  c: ["c"],
  n: ["n", "new"],
};

function rowGroupKey(row) {
  return String(
    row?.group ?? row?.groupName ?? row?.groupCode ?? row?.groupId ?? ""
  )
    .toLowerCase()
    .trim();
}

function matchesGroupKey(rowKey, groupKey) {
  if (!rowKey) return false;
  if (rowKey === groupKey) return true;
  return GROUP_API_ALIASES[groupKey]?.includes(rowKey) ?? false;
}

function pickGroupRowFromArray(rows, groupKey, index) {
  const matched = rows.find((row) => matchesGroupKey(rowGroupKey(row), groupKey));
  return matched || rows[index] || {};
}

function pickGroupRowFromObject(source, groupKey) {
  if (!source || typeof source !== "object" || Array.isArray(source)) return {};
  for (const alias of GROUP_API_ALIASES[groupKey] || [groupKey]) {
    if (source[alias]) return source[alias];
  }
  return {};
}

export function formatGroupLabel(value) {
  if (value == null || value === "" || value === 0) return "";
  const key = String(value).toLowerCase().trim();
  if (DAILY_GROUP_LABELS[key]) return DAILY_GROUP_LABELS[key];
  if (key.length === 1) return `Group ${key.toUpperCase()}`;
  return `Group ${String(value).charAt(0).toUpperCase()}${String(value).slice(1)}`;
}

/** Maps dashboard group keys to Consultant Management `?group=` query values. */
export function getConsultantManagementGroupParam(groupKey) {
  const key = String(groupKey ?? "").toLowerCase().trim();
  if (!key) return "";
  if (key === "n" || key === "new") return "new";
  return key;
}

export function normalizeDailyGroupPerformanceData(result = {}) {
  const raw =
    result.dailyGroupPerformance ??
    result.dailyGroupBreakdown ??
    result.groups ??
    result.dailyBreakdown ??
    result.data;

  const withGroupField = (row, groupKey) => ({
    ...row,
    group: groupKey,
  });

  const source = raw ?? result;

  return DAILY_GROUP_ORDER.map((groupKey, index) => {
    let rowData = {};
    if (Array.isArray(source)) {
      rowData = pickGroupRowFromArray(source, groupKey, index);
    } else if (source && typeof source === "object") {
      rowData =
        pickGroupRowFromObject(source, groupKey) ||
        pickGroupRowFromObject(result, groupKey);
    }
    return withGroupField(rowData, groupKey);
  });
}

const pair = (totalCol, uniqueCol) => [totalCol, uniqueCol];

// export const DAILY_GROUP_COLUMN_DEFINITIONS = [
//   {
//     key: "group",
//     label: "Group",
//     type: "group",
//     description:
//       "Consultant performance group for this row: Group A, Group B, Group C, or Group N.",
//     example: "Group N shows metrics for consultants in the new (N) group.",
//   },

//   {
//     key: "totalConsultants",
//     label: "Total Consultants",
//     type: "number",
//     className: "primary",
//     metricGroupStart: true,
//     description:
//       "Total number of consultants belonging to this group.",
//     example: "If Group A has 15 consultants, this shows 15.",
//   },



//   ...pair(
//     {
//       key: "totalConsultationAttempt",
//       label: "Total Consultation Attempt",
//       type: "number",
//       metricGroupStart: true,
//       description:
//         "Total consultation attempts recorded for the group on this date, including repeat attempts by the same user.",
//       example: "If one user attempts twice and another attempts once, the total is 3.",
//     },
//     {
//       key: "uniqueConsultationAttempt",
//       label: "Unique Consultation Attempt",
//       type: "number",
//       className: "primary",
//       description:
//         "Number of distinct users who attempted a consultation in the group on this date.",
//       example: "If 3 attempts came from 2 users, the unique count is 2.",
//     }
//   ),
//   ...pair(
//     {
//       key: "totalMissedConsultation",
//       label: "Total Missed Consultation",
//       type: "number",
//       className: "error",
//       metricGroupStart: true,
//       description:
//         "Total missed consultation events for the group on this date.",
//       example: "If one user misses twice and another misses once, the total missed count is 3.",
//     },
//     {
//       key: "uniqueMissedConsultation",
//       label: "Unique Missed Consultation",
//       type: "number",
//       className: "error",
//       description:
//         "Number of distinct users with at least one missed consultation in the group on this date.",
//       example: "If 3 missed events came from 2 users, the unique missed count is 2.",
//     }
//   ),
//   ...pair(
//     {
//       key: "totalCanceledConsultation",
//       label: "Total Canceled Consultation",
//       type: "number",
//       className: "error",
//       metricGroupStart: true,
//       description:
//         "Total canceled consultation events for the group on this date.",
//       example: "If two users create 3 cancellation events, the total canceled count is 3.",
//     },
//     {
//       key: "uniqueCanceledConsultation",
//       label: "Unique Canceled Consultation",
//       type: "number",
//       className: "error",
//       description:
//         "Number of distinct users with at least one canceled consultation in the group on this date.",
//       example: "If 3 cancellations came from 2 users, the unique canceled count is 2.",
//     }
//   ),
//   ...pair(
//     {
//       key: "totalCompletedConsultation",
//       label: "Total Completed Consultation",
//       type: "number",
//       className: "success",
//       metricGroupStart: true,
//       description:
//         "Total completed consultation sessions for the group on this date.",
//       example: "If 4 sessions were completed that day, the total completed count is 4.",
//     },
//     {
//       key: "uniqueCompletedConsultation",
//       label: "Unique Completed Consultation",
//       type: "number",
//       className: "success",
//       description:
//         "Number of distinct users who completed at least one consultation in the group on this date.",
//       example: "If 4 completions came from 3 users, the unique completed count is 3.",
//     }
//   ),
//   {
//     key: "firstConsultation",
//     label: "1st Consultation",
//     type: "number",
//     className: "primary",
//     metricGroupStart: true,
//     description:
//       "Completed first consultation sessions for the group on this date.",
//     example: "If 3 users completed their first consultation, this shows 3.",
//   },
//   {
//     key: "firstConsultationRevenue",
//     label: "1st Consultation Revenue",
//     type: "currency",
//     className: "primary",
//     description:
//       "Total revenue generated from first consultations for the group on this date.",
//     example: "If 3 first consultations each generated Rs. 300, this shows Rs. 900.",
//   },
//   {
//     key: "secondConsultation",
//     label: "2nd Consultation",
//     type: "number",
//     className: "primary",
//     description:
//       "Completed second consultation sessions for the group on this date.",
//     example: "If 4 second consultations were completed, this shows 4.",
//   },
//   {
//     key: "secondConsultationRevenue",
//     label: "2nd Consultation Revenue",
//     type: "currency",
//     className: "primary",
//     description:
//       "Total revenue generated from second consultations for the group on this date.",
//     example: "If 4 second consultations each generated Rs. 250, this shows Rs. 1,000.",
//   },
//   {
//     key: "thirdConsultation",
//     label: "3rd Consultation",
//     type: "number",
//     className: "primary",
//     description:
//       "Completed third consultation sessions for the group on this date.",
//     example: "If 2 users completed their third consultation, this shows 2.",
//   },
//   {
//     key: "thirdConsultationRevenue",
//     label: "3rd Consultation Revenue",
//     type: "currency",
//     className: "primary",
//     description:
//       "Total revenue generated from third consultations for the group on this date.",
//     example: "If 2 third consultations each generated Rs. 200, this shows Rs. 400.",
//   },
//   {
//     key: "fourthConsultation",
//     label: "4th & Onwards Consultation",
//     type: "number",
//     className: "primary",
//     description:
//       "Completed fourth and later consultation sessions for the group on this date.",
//     example: "A user's fifth completed consultation counts in this column.",
//   },
//   {
//     key: "fourthConsultationRevenue",
//     label: "4th & Onwards Consultation Revenue",
//     type: "currency",
//     className: "primary",
//     description:
//       "Total revenue generated from fourth and later consultations for the group on this date.",
//     example: "If 4th-onwards consultations generated Rs. 500 total, this shows Rs. 500.",
//   },
//   {
//     key: "queryCompletedConsultation",
//     label: "Query Completed",
//     type: "number",
//     className: "primary",
//     metricGroupStart: true,
//     description:
//       "Total completed query consultations for the group on this date.",
//     example: "If 5 query consultations were completed, this shows 5.",
//   },
//   {
//     key: "sameDayRepeatConsultation",
//     label: "Same Day 1st to 2nd Conversion",
//     type: "number",
//     className: "primary",
//     metricGroupStart: true,
//     description:
//       "Users who completed both their first and second consultation in the group on the same date.",
//     example: "A user completes the first consultation in the morning and the second later that day, adding 1.",
//   },
//   {
//     key: "sameDayConversionPct",
//     label: "Same Day 1st to 2nd Conversion %",
//     type: "percentage",
//     description:
//       "Percentage of first consultations that converted to a second consultation on the same date.",
//     example: "10 first consultations and 3 same-day second consultations show 30%.",
//   },
//   {
//     key: "totalSpending",
//     label: "Total Spending",
//     type: "currency",
//     metricGroupStart: true,
//     omitOnMarketingDashboard: true,
//     description:
//       "Total wallet spending linked to the group on this date.",
//     example: "If users spent Rs. 1,200 on consultations, this shows Rs. 1,200.",
//   },
// ];

export const DAILY_GROUP_COLUMN_DEFINITIONS = [
  {
    key: "group",
    label: "Group",
    type: "group",
    description:
      "Which team of astrologers this row belongs to — Group A, B, C, N (new astrologers), or Dead.",
    example:
      "Group A row shows numbers only for astrologers placed in Group A.",
  },

  {
    key: "totalConsultants",
    label: "Total Consultants",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "How many astrologers are in this group right now.",
    example: "15 astrologers in Group A → this shows 15.",
  },

  ...pair(
    {
      key: "totalConsultationAttempt",
      label: "Total Consultation Attempt",
      type: "number",
      metricGroupStart: true,
      description:
        "How many times customers tried to connect with astrologers in this group — chat or call, successful or not. If one customer tries 3 times, all 3 are counted.",
      example:
        "Customer books chat, cancels, then books call → Total Attempt = 2.",
    },
    {
      key: "uniqueConsultationAttempt",
      label: "Unique Consultation Attempt",
      type: "number",
      className: "primary",
      description:
        "How many different customers tried at least once. Same customer trying multiple times still counts as 1 person here.",
      example: "3 attempts from 2 customers → Unique = 2.",
    }
  ),

  ...pair(
    {
      key: "totalMissedConsultation",
      label: "Total Missed Consultation",
      type: "number",
      className: "error",
      metricGroupStart: true,
      description:
        "Sessions where the astrologer did not pick up — customer waited but call/chat was not answered. This is the astrologer's miss, not the customer canceling.",
      example:
        "Astrologer misses the same customer's call twice → Missed = 2.",
    },
    {
      key: "uniqueMissedConsultation",
      label: "Unique Missed Consultation",
      type: "number",
      className: "error",
      description:
        "How many different customers had at least one missed session with this group.",
      example: "3 missed sessions from 2 customers → Unique Missed = 2.",
    }
  ),

  ...pair(
    {
      key: "totalCanceledConsultation",
      label: "Total Canceled Consultation",
      type: "number",
      className: "error",
      metricGroupStart: true,
      description:
        "Sessions the customer canceled themselves — before or during the session. Does not include astrologer missed calls.",
      example:
        "Customer cancels chat before connecting → counts here, not in Missed.",
    },
    {
      key: "uniqueCanceledConsultation",
      label: "Unique Canceled Consultation",
      type: "number",
      className: "error",
      description:
        "How many different customers canceled at least one session with this group.",
      example: "2 customers each cancel once → Unique Canceled = 2.",
    }
  ),

  ...pair(
    {
      key: "totalCompletedConsultation",
      label: "Total Completed Consultation",
      type: "number",
      className: "success",
      metricGroupStart: true,
      description:
        "Sessions that actually finished — both free (5-minute trial) and paid (chat or call). Gifts are not counted here; gifts are separate spending.",
      example:
        "2 free chats + 3 paid calls completed → Total Completed = 5.",
    },
    {
      key: "uniqueCompletedConsultation",
      label: "Unique Completed Consultation",
      type: "number",
      className: "success",
      description:
        "How many different customers completed at least one session (free or paid) with this group.",
      example: "5 completed sessions from 3 customers → Unique Completed = 3.",
    }
  ),

  {
    key: "firstConsultation",
    label: "1st Consultation",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "Customer's first paid offer session that completed — special introductory price on chat or call. Free 5-minute trial does not count. Second-offer sessions also do not count here.",
    example:
      "Customer does first-offer chat at intro price → counts 1. Free trial chat does not count.",
  },
  {
    key: "firstConsultationRevenue",
    label: "1st Consultation Spending",
    type: "currency",
    className: "primary",
    description:
      "Money customers spent on those first-offer sessions (chat or call). Only consultation charges — gifts, meditation, vastu, and other products are not included.",
    example:
      "First-offer call earns ₹300 → shows ₹300 here. ₹100 gift on same day is not added.",
  },
  {
    key: "secondConsultation",
    label: "2nd Consultation",
    type: "number",
    className: "primary",
    description:
      "Customer's second special-offer session that completed — usually the discounted audio/call offer after the first offer. Free trial and first offer do not count here.",
    example:
      "Customer completes second-offer call → counts 1 in 2nd Consultation.",
  },
  {
    key: "secondConsultationRevenue",
    label: "2nd Consultation Spending",
    type: "currency",
    className: "primary",
    description:
      "Money spent on second-offer consultation sessions only. Gifts and other products are not included. If that session is actually the customer's 3rd or 4th visit, its revenue goes in that column instead.",
    example:
      "Second-offer call costs ₹250 → shows ₹250 here. Gift money is separate.",
  },
  {
    key: "thirdConsultation",
    label: "3rd Consultation",
    type: "number",
    className: "primary",
    description:
      "Customer's 3rd paid session with this astrologer group — the session right after they finished first offer and second offer, in order of when sessions happened. Can be chat or call at regular price.",
    example:
      "After first-offer chat and second-offer call, customer does a normal paid chat → that is the 3rd consultation.",
  },
  {
    key: "thirdConsultationRevenue",
    label: "3rd Consultation Spending",
    type: "currency",
    className: "primary",
    description:
      "Money customers spent on their 3rd consultation sessions. Consultation charges only — no gifts or other products.",
    example:
      "3rd session costs ₹200 → shows ₹200 here.",
  },
  {
    key: "fourthConsultation",
    label: "4th & Onwards Consultation",
    type: "number",
    className: "primary",
    description:
      "All paid sessions from the 4th visit onwards — repeat customers coming back after first offer, second offer, and third session. Regular-price chat and call both count.",
    example:
      "Customer's 5th paid session with the astrologer → counts in 4th & Onwards.",
  },
  {
    key: "fourthConsultationRevenue",
    label: "4th & Onwards Consultation Spending",
    type: "currency",
    className: "primary",
    description:
      "Money earned from 4th and later consultation sessions. Consultation only — gifts not included.",
    example:
      "4th and later sessions together earn ₹500 → shows ₹500 here.",
  },
  {
    key: "otherSpending",
    label: "Other Spending",
    type: "currency",
    className: "primary",
    description:
      "Wallet spending not tied to a ranked private-call consultation, such as gifts, meditation, vastu, session bookings, refunds, queries, or unlinked debits. This plus all four consultation-spending columns equals Total Spending.",
    example:
      "₹800 consultation spending + ₹200 gifts/services → Other Spending = ₹200 and Total Spending = ₹1,000.",
  },
  {
    key: "queryCompletedConsultation",
    label: "Query Completed",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "Written query questions that the astrologer answered and marked complete. Separate from chat/call offer funnel.",
    example:
      "Customer asks a question in writing and astrologer completes the answer → counts 1.",
  },
  {
    key: "queryConvertedUsers",
    label: "Query Converted",
    type: "number",
    className: "success",
    description:
      "Completed queries in this group that converted: the user then recharged and finished their first paid consultation with the same consultant.",
    example:
      "A written query is completed, the user recharges, and then completes a paid chat or call with the same astrologer → counts 1.",
  },
  {
    key: "giftPayout",
    label: "Gift Payout (50%)",
    type: "currency",
    className: "primary",
    description: "Combined 50% consultant earning from gifts received in the selected period.",
    example: "Users send ₹1,000 in gifts → group payout is ₹500.",
  },
  {
    key: "sameDayRepeatConsultation",
    label: "Same Day 1st to 2nd Conversion",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "Customers who did both first offer and second offer on the same day with astrologers in this group. Shows how many people upgraded quickly in one day.",
    example:
      "Morning first-offer chat + evening second-offer call, same day → adds 1.",
  },
  {
    key: "sameDayConversionPct",
    label: "Same Day 1st to 2nd Conversion %",
    type: "percentage",
    description:
      "Out of all first-offer completions, what percentage also did second offer the same day. Higher % means customers are converting faster.",
    example:
      "10 first offers and 3 same-day upgrades → 30%.",
  },
  {
    key: "totalSpending",
    label: "Total Spending",
    type: "currency",
    metricGroupStart: true,
    omitOnMarketingDashboard: true,
    description:
      "All user wallet spending attributed to this group's consultants, using the same spending definition as Daily Performance.",
    example:
      "₹800 consultations + ₹200 gifts + ₹200 other services → Total Spending = ₹1,200.",
  },
];






/** Main dashboard: all columns. Marketing dashboard: hides columns flagged with omitOnMarketingDashboard. */
export function getDailyGroupColumnDefinitions(isMarketingDashboard) {
  if (!isMarketingDashboard) return DAILY_GROUP_COLUMN_DEFINITIONS;
  return DAILY_GROUP_COLUMN_DEFINITIONS.filter(
    (c) => !c.omitOnMarketingDashboard
  );
}

export function buildDailyGroupColumnVisibilityDefaults() {
  const vis = {};
  DAILY_GROUP_COLUMN_DEFINITIONS.forEach((col) => {
    vis[col.key] = true;
  });
  return vis;
}
